import React, { useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGoogleReCaptcha, withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TextField from '@amf/shared/components/form/TextField';
import TextArea from '@amf/shared/components/form/TextArea';
import Button, { ButtonProps } from '@amf/shared/components/button/Button';

const schema = yup.object({
  name: yup.string().required('contact.form.name.required').max(64, 'contact.form.name.max'),
  email: yup.string().email('contact.form.email.invalid').required('contact.form.email.required'),
  phone: yup
    .string()
    .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g, 'contact.form.phone.invalid')
    .required('contact.form.phone.required'),
  message: yup
    .string()
    .required('contact.form.message.required')
    .max(512, 'contact.form.message.max'),
});

export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface ContactFormProps {
  values: ContactFormData;
  onSubmit: (values: ContactFormData) => Promise<void>;
  buttonProps?: ButtonProps;
}

function ContactForm({ values, onSubmit, buttonProps }: ContactFormProps) {
  const { t } = useTranslate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [sending, setSending] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: yupResolver(schema),
    defaultValues: values,
  });

  const verifyReCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    await executeRecaptcha('contactForm');
  }, [executeRecaptcha]);

  useEffect(() => {
    verifyReCaptcha();
  }, [verifyReCaptcha]);

  const saveForm = useCallback(
    async (data: ContactFormData) => {
      setSending(true);
      await onSubmit(data);
      setSending(false);
    },
    [onSubmit],
  );

  return (
    <div className='ContactPage__form'>
      <form onSubmit={handleSubmit(saveForm)}>
        <div className='ContactPage__formRow'>
          <div className='ContactPage__formCol'>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextField
                  className='ContactPage__formField'
                  label={t('contact.form.name')}
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='ContactPage__formRow'>
          <div className='ContactPage__formCol'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  className='ContactPage__formField'
                  label={t('contact.form.email')}
                  error={errors.email?.message}
                  type='email'
                  {...field}
                />
              )}
            />
          </div>
          <div className='ContactPage__formCol'>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <TextField
                  className='ContactPage__formField'
                  label={t('contact.form.phone')}
                  error={errors.phone?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='ContactPage__formRow'>
          <div className='ContactPage__formCol'>
            <Controller
              name='message'
              control={control}
              render={({ field }) => (
                <TextArea
                  className='ContactPage__formField'
                  label={t('contact.form.message')}
                  error={errors.message?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='ContactPage__formRow'>
          <div className='ContactPage__formCol'>
            <Button type='submit' variant='blue' disabled={sending} {...buttonProps}>
              {t('contact.form.send')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default withGoogleReCaptcha<ContactFormProps>(ContactForm);
